import { useCallback, useRef, useState } from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import {
  describeInboxHandOffError,
  describeSendToInboxError
} from 'src/components/pedigree-chart/pedigree-sidebar/sections/test-results/labReportInboxText';
import { namesScope } from 'src/components/pedigree-chart/pedigree-sidebar/sections/clinical-notes-section/forms/prenatalScope';
import { describeTestResultError } from './useStructuredTestResults';
import { translate } from 'src/hooks/useTranslate';

// A lab report uploaded on a Test, read into a proposal that is APPLIED TO THE TEST FORM the moment
// it arrives (TGCLOUD-1866, Phase 2 rework: "the Test record is the home of the result"; TGCLOUD-1868
// plan D3: applied on open, each part then confirmed where it is on the form).
//
// A REVIEW'S STATUS
//   reading    the report is being read
//   choosing   an uploaded report gives results for two or more people, and intake asks whose this
//              Test is (TGCLOUD-1868, Phase E4): `choice` is { individuals, proposedKey }, as intake
//              answered it (no token and no proposal), and `error` why a choice was refused, if one
//              was. Nothing can be applied from it until a person is chosen (chooseIndividual)
//   failed     it could not be read (or the hand-off was refused): `error` says why
//   ready      its reading has arrived, and the form has not taken it yet (another report is
//              applied: one report per save)
//   applied    the form holds it (markApplied): `application` keeps what the form did with it, so
//              a choice can be made again and the report taken off again (applyProposal.js)
//
//   intake(file, { testNoteId, attachmentName, testScope })
//                                  POST /test-results/intake. Nothing clinical is stored by it:
//                                  the server keeps the report file (write-once) and answers with a
//                                  proposal in the Test form's shape and a signed token. The review
//                                  keeps the report's bytes as `file`, a Blob with no name, for the
//                                  verification view to draw (TGCLOUD-1868 D1): nothing is fetched
//                                  back for a report that is already in the browser. `testScope`
//                                  is the Test's prenatal scope, sent only where it names one (the
//                                  server holds a Test not saved yet to it when it says whose part
//                                  of a report fits the Test), so an ordinary Test's upload sends
//                                  exactly what it always did. A report about two or more people
//                                  answers who it covers instead: the review is then `choosing`.
//   chooseIndividual(key, individual, { testScope })
//                                  the person of the report this Test is for, chosen: intake again
//                                  with `individualKey` (and the Test's scope), from the review's own
//                                  bytes. The review keeps `individual` ({ key, role }) to say whom
//                                  the Test is for. A refusal goes back to the choice, with why.
//   readQueued(itemId, { testNoteId, individual })
//                                  THE HAND-OFF (Phase 3): a report matched to this person in the
//                                  lab report inbox, read for this Test from the server's stored
//                                  copy (POST /test-results/inbox/:id/intake). The answer is
//                                  intake's own shape, so applying and saving are unchanged; the
//                                  token names the inbox item, and the save links it. For one
//                                  person of a report about several (TGCLOUD-1868, Phase E4) the
//                                  deep link's `individual` ({ key, role, pregnancyId?, fetusId? })
//                                  says whose part is read: sent as `individualKey`, and a fetus's
//                                  fetus record as `pregnancyId` and `fetusId`. The review keeps
//                                  `individual` ({ key, role }) to say whom the Test is for. A
//                                  report about one person sends exactly what it always did.
//   sendToInbox(key, { onSent })   "Send to the inbox instead" (Phase 3): an uploaded report that is
//                                  someone else's becomes an inbox item, from the stored file the
//                                  review's token names (POST /test-results/inbox/from-intake), and
//                                  the review is dropped. `onSent(review)` is called once the server
//                                  has made the item and before the review goes, so the verification
//                                  view's telemetry can say where it went (TGCLOUD-1868 D7)
//   sendChoiceToInbox(key, { onSent })
//                                  the same, while whose the Test is is still chosen: the question
//                                  carries no token, so the review's own file is sent as it is
//                                  (POST /test-results/inbox/from-upload), read for nobody, never
//                                  applied (TGCLOUD-1868, Phase E routine call and its review)
//   setAcknowledged / setWarningAcknowledged / setChoices   the clinician's answers. A choice is
//                                  made ON THE FORM (applyProposal.withChoice); the review keeps it
//                                  so the report can be taken off again as it was chosen
//   markApplied(key, application)  the proposal is in the form; the token now rides on the Test's
//                                  save as `labReportApplication`. `application` is
//                                  { plan, before, listState, summary } (TestForm)
//   finishApplied()                the save recorded it
//   dismiss(key)                   drop a review; nothing was stored for it but the file. The form
//                                  takes an applied report off itself first (unapplyReport)
//
// NOTHING IS WRITTEN HERE. Applying fills the open form, and SAVING the Test is what records the
// report (PUT /clinical-notes/test with labReportApplication). Closing the form discards the
// reviews with it. One report is applied per save: the save carries one token. Values the reader
// was not confident of arrive EMPTY in the proposal and are typed or chosen on the form itself, so
// a review holds no draft of its own.
//
// THE FILE GOES WITHOUT ITS NAME. A lab report file is often named after the patient, and the
// server keeps no file name for a report (backend/middleware/labReportUpload.js). It is sent as
// NEUTRAL_NAME, which is still a .pdf for the upload filter's extension check, so the name never
// leaves the browser on this request.

export const NEUTRAL_NAME = 'lab-report.pdf';

let nextReviewKey = 0;

// The person of a report about several a reading is for, as a review keeps it: their key and role.
const individualOf = (individual) =>
  individual && typeof individual.key === 'string' && individual.key
    ? { key: individual.key, role: individual.role || null }
    : null;

// What the hand-off is told of that person: their key, and a fetus's fetus record (both or neither).
const handOffIndividualFields = (individual) =>
  individualOf(individual)
    ? {
        individualKey: individual.key,
        ...(individual.pregnancyId && individual.fetusId
          ? {
              pregnancyId: String(individual.pregnancyId),
              fetusId: String(individual.fetusId)
            }
          : {})
      }
    : {};

// The Test's prenatal scope as an upload sends it: only the fields that name something, and none at
// all for an ordinary Test (prenatalScope.namesScope, carriesPrenatalScope's own rule).
const scopeFields = (testScope) =>
  ['pregnancyId', 'fetusId']
    .filter((field) => namesScope(testScope && testScope[field]))
    .map((field) => [field, String(testScope[field])]);

// A review once its reading has arrived, whichever route read it.
const readyWith = (data) => (r) => ({
  ...r,
  status: 'ready',
  reading: data,
  acknowledged: false,
  acknowledgedWarnings: new Set(),
  choices: {},
  application: null,
  error: null
});

// A review whose report gives results for two or more people: whose this Test is, still to choose.
// The answer carries no token (plan, "Upload of a multi-individual report on a Test"); one that does
// is kept, for "Send to the inbox instead" (sendChoiceToInbox), and is never applied.
const choosingWith = (data) => (r) => ({
  ...r,
  status: 'choosing',
  reading: null,
  choice: {
    individuals: Array.isArray(data.individuals) ? data.individuals : [],
    proposedKey:
      typeof data.proposedKey === 'string' && data.proposedKey
        ? data.proposedKey
        : null,
    token: typeof data.token === 'string' && data.token ? data.token : null
  },
  individual: null,
  error: null
});
// What arrived: a reading, or the question whose part of the report this Test is.
const answered = (data) =>
  data && data.needsIndividual === true ? choosingWith(data) : readyWith(data);

export function useLabReportReview({ familyMemberId }) {
  const [reviews, setReviews] = useState([]);
  // The reviews as last drawn, for an action that needs a review's token after an await.
  const reviewsRef = useRef(reviews);
  reviewsRef.current = reviews;

  const update = useCallback(
    (key, fn) =>
      setReviews((prev) => prev.map((r) => (r.key === key ? fn(r) : r))),
    []
  );

  // `origin` says where the report came from: an upload on this Test, or the lab report inbox.
  // `attachmentName` is the uploaded file's name on this Test, for "Send to the inbox instead".
  const intake = useCallback(
    async (file, { testNoteId, attachmentName = null, testScope = null }) => {
      if (!file || !familyMemberId || !testNoteId) return null;
      const key = `lab-report-${(nextReviewKey += 1)}`;
      setReviews((prev) => [
        ...prev,
        {
          key,
          status: 'reading',
          testNoteId,
          origin: 'upload',
          attachmentName,
          // The bytes alone: a Blob carries no file name, which is often the patient's.
          file: new Blob([file], { type: 'application/pdf' })
        }
      ]);
      const form = new FormData();
      form.append('familyMemberId', familyMemberId);
      form.append('testNoteId', testNoteId);
      for (const [field, value] of scopeFields(testScope)) {
        form.append(field, value);
      }
      form.append('file', file, NEUTRAL_NAME);
      try {
        const { data } = await axios.post(APICONSTANTS.testResultIntake, form);
        update(key, answered(data));
      } catch (err) {
        update(key, (r) => ({
          ...r,
          status: 'failed',
          error: describeTestResultError(
            err,
            translate('The lab report could not be read.')
          )
        }));
      }
      return key;
    },
    [familyMemberId, update]
  );

  // WHOSE PART OF THE REPORT THIS TEST IS, CHOSEN (TGCLOUD-1868, Phase E4). The report is read again
  // for that person, from the bytes the review holds (nothing is fetched back), with the Test's
  // scope, which the server holds a Test not saved yet to. Only a review still choosing is read for
  // a person, once. A refusal (the person does not fit the Test, their result is already applied to
  // another Test of the family) goes back to the choice, said for them.
  const chooseIndividual = useCallback(
    async (key, individual, { testScope = null } = {}) => {
      const review = reviewsRef.current.find((r) => r.key === key);
      const forWhom = individualOf(individual);
      if (
        !review ||
        review.status !== 'choosing' ||
        !review.file ||
        !forWhom ||
        !familyMemberId
      ) {
        return null;
      }
      update(key, (r) => ({
        ...r,
        status: 'reading',
        individual: forWhom,
        error: null
      }));
      const form = new FormData();
      form.append('familyMemberId', familyMemberId);
      form.append('testNoteId', review.testNoteId);
      form.append('individualKey', forWhom.key);
      for (const [field, value] of scopeFields(testScope)) {
        form.append(field, value);
      }
      form.append('file', review.file, NEUTRAL_NAME);
      try {
        const { data } = await axios.post(APICONSTANTS.testResultIntake, form);
        update(key, (r) => ({
          ...answered(data)(r),
          // Read for this person: never asked whose again.
          ...(data && data.needsIndividual === true
            ? {}
            : { individual: forWhom, choice: r.choice })
        }));
      } catch (err) {
        update(key, (r) => ({
          ...r,
          status: 'choosing',
          individual: null,
          error: describeTestResultError(
            err,
            translate(
              'The lab report could not be read for the person chosen.'
            ),
            { individual: forWhom }
          )
        }));
      }
      return key;
    },
    [familyMemberId, update]
  );

  // THE HAND-OFF. Nothing is uploaded: the server reads the copy it stored when the report arrived
  // in the inbox, checks it is matched to THIS person now, and answers as intake does.
  const readQueued = useCallback(
    async (itemId, { testNoteId, individual = null }) => {
      if (!itemId || !familyMemberId || !testNoteId) return null;
      const key = `lab-report-${(nextReviewKey += 1)}`;
      const forWhom = individualOf(individual);
      setReviews((prev) => [
        ...prev,
        {
          key,
          status: 'reading',
          testNoteId,
          origin: 'inbox',
          inboxItemId: itemId,
          ...(forWhom ? { individual: forWhom } : {})
        }
      ]);
      try {
        const { data } = await axios.post(
          APICONSTANTS.labReportInboxHandOff.replace(
            ':id',
            encodeURIComponent(itemId)
          ),
          { familyMemberId, testNoteId, ...handOffIndividualFields(individual) }
        );
        update(key, readyWith(data));
      } catch (err) {
        update(key, (r) => ({
          ...r,
          status: 'failed',
          error: describeInboxHandOffError(
            err,
            forWhom ? { individual: forWhom } : {}
          )
        }));
      }
      return key;
    },
    [familyMemberId, update]
  );

  // "Send to the inbox instead". The review is dropped only once the server has made (or found)
  // the inbox item; a refusal stays on the review, beside the button.
  const sendToInbox = useCallback(
    async (key, { onSent } = {}) => {
      const review = reviewsRef.current.find((r) => r.key === key);
      const token = review?.reading?.token;
      if (!review || !token) return { ok: false };
      update(key, (r) => ({ ...r, sending: true, sendError: null }));
      try {
        const { data } = await axios.post(
          APICONSTANTS.labReportInboxFromIntake,
          { token }
        );
        onSent?.(review);
        setReviews((prev) => prev.filter((r) => r.key !== key));
        return { ok: true, data: data || {}, review };
      } catch (err) {
        update(key, (r) => ({
          ...r,
          sending: false,
          sendError: describeSendToInboxError(err)
        }));
        return { ok: false };
      }
    },
    [update]
  );

  // "Send to the inbox instead" WHILE WHOSE THE TEST IS IS CHOSEN (TGCLOUD-1868, Phase E routine
  // call). The question "which one is this Test for?" carries no reading's token, so the review's own
  // file goes to the inbox as it is (POST /inbox/from-upload): read for nobody, stored and audited as
  // no patient's, never refused because someone's part of it is filed already (TGCLOUD-1868 review:
  // it used to be read for a person first, only to get a token, AI pass and all). Nothing of the
  // report goes on the Test. A choice that came with a token of its own is sent with it
  // (/inbox/from-intake). The review goes only once the server has made (or found) the item; a
  // refusal stays on the choice.
  const sendChoiceToInbox = useCallback(
    async (key, { onSent } = {}) => {
      const review = reviewsRef.current.find((r) => r.key === key);
      if (!review || review.status !== 'choosing') return { ok: false };
      const choiceToken = review.choice?.token || null;
      if (!choiceToken && !review.file) return { ok: false };
      update(key, (r) => ({ ...r, sending: true, sendError: null }));
      try {
        let data;
        if (choiceToken) {
          ({ data } = await axios.post(APICONSTANTS.labReportInboxFromIntake, {
            token: choiceToken
          }));
        } else {
          const form = new FormData();
          form.append('file', review.file, NEUTRAL_NAME);
          ({ data } = await axios.post(
            APICONSTANTS.labReportInboxFromUpload,
            form
          ));
        }
        onSent?.(review);
        setReviews((prev) => prev.filter((r) => r.key !== key));
        return { ok: true, data: data || {}, review };
      } catch (err) {
        update(key, (r) => ({
          ...r,
          sending: false,
          sendError: describeSendToInboxError(err)
        }));
        return { ok: false };
      }
    },
    [update]
  );

  const setAcknowledged = useCallback(
    (key, acknowledged) =>
      update(key, (r) => ({ ...r, acknowledged: acknowledged === true })),
    [update]
  );

  const setWarningAcknowledged = useCallback(
    (key, warning, acknowledged) =>
      update(key, (r) => {
        const next = new Set(r.acknowledgedWarnings || []);
        if (acknowledged === true) next.add(warning);
        else next.delete(warning);
        return { ...r, acknowledgedWarnings: next };
      }),
    [update]
  );

  /** Choose the Test's value or the report's for conflicts: { [choiceId]: 'current'|'report' }. */
  const setChoices = useCallback(
    (key, patch) =>
      update(key, (r) => ({ ...r, choices: { ...r.choices, ...patch } })),
    [update]
  );

  // Only a review whose reading has arrived is applied, once.
  const markApplied = useCallback(
    (key, application) =>
      update(key, (r) =>
        r.status === 'ready'
          ? { ...r, status: 'applied', application: application || null }
          : r
      ),
    [update]
  );

  const finishApplied = useCallback(
    () => setReviews((prev) => prev.filter((r) => r.status !== 'applied')),
    []
  );

  const dismiss = useCallback(
    (key) => setReviews((prev) => prev.filter((r) => r.key !== key)),
    []
  );

  return {
    reviews,
    appliedReview: reviews.find((r) => r.status === 'applied') || null,
    intake,
    chooseIndividual,
    readQueued,
    sendToInbox,
    sendChoiceToInbox,
    setAcknowledged,
    setWarningAcknowledged,
    setChoices,
    markApplied,
    finishApplied,
    dismiss
  };
}
